/**
 * Main game controller.
 *
 * Orchestrates the game loop: state management, player updates,
 * level loading, terrain, obstacle collision, triggers, and rendering.
 */

import { Engine } from '@/core/engine';
import { StateMachine } from '@/core/state';
import { InputManager } from '@/core/input';
import { Renderer } from '@/rendering/renderer';
import { Player, playerTuning } from './player';
import {
  checkCollision, checkGraze, checkJumpPadHit, checkOrbOverlap,
  updateObstacles, type Obstacle, type PadSize, type OrbSize,
} from './obstacles';
import { hexToColor, type Color, type Vec2 } from '@/types';
import { rgbToHue, deriveHillPalette, hslToColor } from '@/types/color-palette';
import { TuningPanel, type TunableParam } from '@/ui/tuning-panel';
import { ParticleSystem } from './particles';
import type { LevelData, RuntimeLevel } from './level';
import { loadLevel, buildObstacles, buildCollectibles, buildTriggers } from './level-loader';
import type { RuntimeCollectible, RuntimeTrigger } from './level-loader';
import { getGroundY, getCeilingY, getGroundTangentAngle, renderTerrain } from './terrain';
import { TunnelRenderer, type TunnelObstacle3D } from './tunnel';
import { AudioEngine } from '@/audio/audio-engine';
import { BeatScheduler } from '@/audio/beat-scheduler';
import { SFX } from '@/audio/sfx';
import { calculateScore, type RunData, type ScoreBreakdown } from './scoring';
import type { ActiveReward } from '@/storage/save-data';
import { applyRewardToPlayer } from './reward-application';

// Import the built-in level
import firstDerivativeData from '../../levels/first-derivative.json';

type ColorEasing = 'linear' | 'sine-in-out' | 'ease-in' | 'ease-out';

function applyColorEasing(t: number, easing: ColorEasing): number {
  switch (easing) {
    case 'sine-in-out': return 0.5 - 0.5 * Math.cos(Math.PI * t);
    case 'ease-in':     return t * t;
    case 'ease-out':    return 1 - (1 - t) * (1 - t);
    case 'linear':
    default:            return t;
  }
}

function lerpColor(a: Color, b: Color, t: number): Color {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
    a: a.a + (b.a - a.a) * t,
  };
}

interface ColorTween {
  fromBase?: Color;
  toBase?: Color;
  fromAccent?: Color;
  toAccent?: Color;
  fromBackground?: Color;
  toBackground?: Color;
  elapsed: number;
  duration: number;
  easing: ColorEasing;
}

// ─── Tunable Constants ──────────────────────────────────────
export const gameTuning = {
  scrollSpeed: 300,          // Pixels/second (base)
  groundY: 80,               // Ground level in world coordinates (fallback)
  respawnX: 100,             // Player X position (fixed, world scrolls)
  deathPause: 0.6,           // Seconds to pause on death
  shakeIntensity: 15,        // Screen shake on death
  bloomStrength: 0.6,        // Post-processing bloom
  vignetteIntensity: 0.3,    // Vignette darkness
  gridSpacing: 50,           // Tron grid cell size
  gridIntensity: 0.4,        // Grid line brightness
  cameraLeadX: 290,          // Camera look-ahead distance
};

export class Game {
  private engine: Engine;
  private renderer: Renderer;
  private input: InputManager;
  private state: StateMachine;
  private player: Player;
  private tuningPanel: TuningPanel;
  private particles: ParticleSystem;

  // Audio
  private audio: AudioEngine;
  private beatScheduler: BeatScheduler | null = null;
  private sfx: SFX;
  private audioStarted = false;

  // World state
  private scrollOffset = 0;
  private obstacles: Obstacle[] = [];

  // Ambient particle timer
  private ambientTimer = 0;

  // Level system
  private level: RuntimeLevel | null = null;
  private speedMultiplier = 1.0;
  private collectibles: RuntimeCollectible[] = [];
  private triggers: RuntimeTrigger[] = [];
  private activeText: { text: string; timer: number; duration: number } | null = null;

  // Theme colors
  private baseColor: Color;
  private accentColor: Color;
  private backgroundColor: Color;
  private spikeColor: Color;
  private wallColor: Color;

  // Active color-trigger tween (interpolates base/accent/background over duration)
  private colorTween: ColorTween | null = null;

  // Death/respawn
  private deathTimer = 0;

  // Graze tracking
  private grazeCooldown = 0;
  private grazeCount = 0;
  private peakGrazeStreak = 0;

  // Scoring
  private runStartTime = 0;
  private inputCount = 0;

  // Checkpoint / practice mode
  practiceMode = false;
  private lastCheckpointScroll = 0;
  private practiceUsedThisRun = false;

  // Progress
  private levelProgress = 0;

  // Complex (3D) mode
  private tunnel: TunnelRenderer;
  private mode: 'real' | 'complex' = 'real';
  private tunnelObstacles: TunnelObstacle3D[] = [];
  private player3dX = 0;            // Player position in tunnel cross-section
  private player3dY = 0;
  private transitionProgress = -1;  // -1 = no transition, 0-1 = transitioning
  private transitionDuration = 2.0; // seconds

  // FPS display
  showFps = true;
  private fpsElement: HTMLElement | null = null;

  /** External callback when game state changes */
  onStateChange: ((state: string) => void) | null = null;

  /** Called when level is completed with score breakdown */
  onLevelComplete: ((breakdown: ScoreBreakdown, runData: RunData) => void) | null = null;

  /** Called when user requests exit from play-test (editor sets this). */
  onExitRequested: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new InputManager();
    this.state = new StateMachine();
    this.player = new Player();
    this.tuningPanel = new TuningPanel();
    this.particles = new ParticleSystem();

    this.audio = new AudioEngine();
    this.sfx = new SFX(this.audio);
    this.tunnel = new TunnelRenderer();

    this.baseColor = hexToColor('#00ffff');
    this.accentColor = hexToColor('#ff00ff');
    this.backgroundColor = hexToColor('#0a0a1a');
    this.spikeColor = hexToColor('#ff0066');
    this.wallColor = hexToColor('#ff6600');

    this.engine = new Engine({
      update: (dt) => this.update(dt),
      render: (interp) => this.render(interp),
    });

    this.input.attach(canvas);
    this.setupFpsDisplay();
    this.setupTuningPanel();

    // Load the built-in level by default
    this.loadLevelData(firstDerivativeData as unknown as LevelData);

    this.state.onTransition((_prev, next) => {
      if (next === 'dead') {
        this.deathTimer = 0;
        this.renderer.camera.shake(gameTuning.shakeIntensity);
        this.renderer.chromaticAberration = 0.015;
        // Death shatter particles
        this.particles.spawnShatter(
          this.player.position.x, this.player.position.y,
          this.player.color, 10,
        );
        // Death SFX + audio dip
        this.sfx.death();
        this.audio.dip(0.4);
      }
      this.onStateChange?.(next);
    });
  }

  /** Apply a daily reward's cosmetic effects to the player. */
  applyDailyReward(reward: ActiveReward | null, baseColor: string): void {
    applyRewardToPlayer(this.player, reward, baseColor);
  }

  /** Load a level from parsed JSON data */
  loadLevelData(data: LevelData): void {
    this.level = loadLevel(data);

    // Apply level settings
    this.baseColor = this.level.baseColor;
    this.accentColor = this.level.accentColor;
    this.backgroundColor = this.level.backgroundColor;
    this.spikeColor = hexToColor('#ff0066');
    this.wallColor = hexToColor('#ff6600');
    this.speedMultiplier = data.settings.initialSpeed;
    this.colorTween = null;

    // Build runtime objects from level data
    this.obstacles = buildObstacles(
      data.segments, this.level.segmentOffsets,
      this.spikeColor, this.wallColor,
    );

    // For obstacles with up=0, they sit on the terrain. Resolve their Y.
    this.resolveObstacleGroundPositions();

    this.collectibles = buildCollectibles(data, this.level.segmentOffsets);
    this.triggers = buildTriggers(data);

    gameTuning.gridIntensity = data.settings.gridIntensity;
  }

  /** Place obstacles that have y=0 onto the terrain surface and compute their tilt angle */
  private resolveObstacleGroundPositions(): void {
    if (!this.level) return;
    for (const obs of this.obstacles) {
      // Obstacle pivot sits at the center of its base
      const pivotX = obs.x + obs.width / 2;

      if (obs.baseY === 0) {
        // Place on terrain at the base-center x position
        const groundY = getGroundY(
          this.level.data.segments, this.level.segmentOffsets,
          pivotX, gameTuning.groundY,
        );
        obs.y = groundY;
        obs.baseY = groundY;
      }

      // Compute tilt angle from ground tangent (only for ground-placed static obstacles)
      if (obs.behavior === null) {
        obs.angle = getGroundTangentAngle(
          this.level.data.segments, this.level.segmentOffsets, pivotX,
        );
      }
    }
  }


  start(opts: { startOffset?: number; onExit?: () => void } = {}): void {
    this.state.reset('playing');
    this.player.respawn(gameTuning.respawnX, gameTuning.groundY + this.player.size + 50);
    this.scrollOffset = opts.startOffset ?? 0;
    this.lastCheckpointScroll = 0;
    this.practiceUsedThisRun = false;
    this.grazeCount = 0;
    this.peakGrazeStreak = 0;
    this.inputCount = 0;
    this.runStartTime = performance.now();
    this.particles.clear();
    this.activeText = null;
    this.ambientTimer = 0;
    this.speedMultiplier = this.level?.data.settings.initialSpeed ?? 1.0;
    this.mode = this.level?.data.settings.initialMode === 'complex' ? 'complex' : 'real';
    this.input.touchMode = this.mode === 'complex' ? 'directional' : 'jump';
    playerTuning.gravity = -Math.abs(playerTuning.gravity); // Ensure downward gravity
    this.transitionProgress = -1;
    this.player3dX = 0;
    this.player3dY = 0;
    this.tunnel.scrollZ = 0;
    this.tunnel.color = { ...this.baseColor, a: 0.5 };
    this.tunnel.ringColor = { ...this.baseColor, a: 0.4 };
    this.tunnel.lineColor = { ...this.baseColor, a: 0.2 };
    this.tunnelObstacles = [];
    // Reset triggers
    for (const t of this.triggers) {
      t.fired = false;
      t.progress = 0;
    }
    // Reset collectibles
    for (const c of this.collectibles) {
      c.collected = false;
    }

    // Skip events before the start offset (play-test resume)
    applyStartOffset(this.triggers, this.collectibles, this.scrollOffset);

    this.onExitRequested = opts.onExit ?? null;

    // Start audio (requires user gesture — start() is called from user interaction)
    this.startAudio();

    this.engine.start();
  }

  stop(): void {
    this.engine.stop();
    this.input.detach();
    this.beatScheduler?.stop();
    this.audio.stop();
    this.audioStarted = false;
    this.tuningPanel.destroy();
    this.renderer.detachListeners();
  }

  private startAudio(): void {
    if (this.audioStarted) {
      this.audio.resume();
      return;
    }

    this.audio.init();
    this.audioStarted = true;

    // Set up beat scheduler with level music params
    const musicParams = this.level?.data.settings.music ?? {
      bpm: 120, key: 'Am', style: 'synthwave', layers: ['bass', 'drums'],
    };

    this.beatScheduler = new BeatScheduler(this.audio, musicParams);

    // Beat sync → visual pulse
    this.beatScheduler.onBeat((event) => {
      this.renderer.beatPulse = event.strength;
      // Beat particles on strong beats
      if (event.strength > 0.3) {
        const cam = this.renderer.camera;
        this.particles.spawnBeatPulse(
          cam.position.x, cam.position.y,
          cam.viewWidth, cam.viewHeight,
          this.baseColor, event.strength,
        );
      }
    });

    this.beatScheduler.start();
  }

  pause(): void {
    if (this.state.state === 'playing') {
      this.state.transition('paused');
    }
  }

  resume(): void {
    if (this.state.state === 'paused') {
      this.state.transition('playing');
    }
  }

  get isPaused(): boolean {
    return this.state.state === 'paused';
  }

  /** Current scroll offset — editor reads this on play-test exit to park the playhead. */
  get currentScrollOffset(): number {
    return this.scrollOffset;
  }

  get currentState(): string {
    return this.state.state;
  }

  toggleTuning(): void {
    this.tuningPanel.toggle();
  }

  private get totalLength(): number {
    return this.level?.totalLength ?? 5000;
  }

  private update(dt: number): void {
    this.input.update();

    if (this.input.isJustPressed('pause')) {
      if (this.state.state === 'playing') {
        this.state.transition('paused');
        return;
      } else if (this.state.state === 'paused') {
        this.state.transition('playing');
      }
    }

    if (this.state.state === 'playing') {
      if (this.mode === 'complex') {
        this.updateComplex(dt);
      } else {
        this.updatePlaying(dt);
        this.updateTransition(dt);
      }
    } else if (this.state.state === 'dead') {
      this.updateDead(dt);
    }

    // Color tween advances whenever time advances, regardless of state.
    this.updateColorTween(dt);

    // Always update particles (they should animate during death too)
    this.particles.update(dt);
  }

  private updateColorTween(dt: number): void {
    const tw = this.colorTween;
    if (!tw) return;
    // Trigger durations are stored in milliseconds (matches `text`/`mode` triggers).
    tw.elapsed += dt * 1000;
    const rawT = Math.min(1, tw.elapsed / tw.duration);
    const t = applyColorEasing(rawT, tw.easing);
    if (tw.fromBase && tw.toBase) this.baseColor = lerpColor(tw.fromBase, tw.toBase, t);
    if (tw.fromAccent && tw.toAccent) this.accentColor = lerpColor(tw.fromAccent, tw.toAccent, t);
    if (tw.fromBackground && tw.toBackground) this.backgroundColor = lerpColor(tw.fromBackground, tw.toBackground, t);
    if (rawT >= 1) this.colorTween = null;
  }

  private updatePlaying(dt: number): void {
    const effectiveSpeed = gameTuning.scrollSpeed * this.speedMultiplier;
    this.scrollOffset += effectiveSpeed * dt;

    // Get ground Y from terrain at player's world position
    const playerWorldX = this.scrollOffset;
    const groundY = this.level
      ? getGroundY(this.level.data.segments, this.level.segmentOffsets, playerWorldX, gameTuning.groundY)
      : gameTuning.groundY;

    // Check ceiling
    const ceilingY = this.level
      ? getCeilingY(this.level.data.segments, this.level.segmentOffsets, playerWorldX)
      : null;

    this.player.effectiveScrollSpeed = effectiveSpeed;

    // Capture pre-update foot position for jump-pad top-land detection
    const prevFeetY = this.player.position.y - this.player.size;

    // Walls act as extended ground when the player's feet are above them.
    // This lets the player land on wall tops while still dying on side hits.
    let effectiveGroundY = groundY;
    if (!this.player.gravityFlipped) {
      const feetY = this.player.position.y - this.player.size;
      for (const obs of this.obstacles) {
        if (obs.type !== 'wall') continue;
        const sx = obs.x - this.scrollOffset + gameTuning.respawnX;
        if (this.player.position.x <= sx || this.player.position.x >= sx + obs.width) continue;
        const wallTop = obs.y + obs.height;
        // Player is horizontally over this wall AND feet are at/above wall top
        if (feetY >= wallTop - 4 && wallTop > effectiveGroundY) {
          effectiveGroundY = wallTop;
        }
      }
    }

    this.player.update(dt, this.input, effectiveGroundY, this.scrollOffset, ceilingY);

    // Orb activation runs AFTER player.update so the player's position is
    // fully advanced this tick — keeping orb-collision in sync with what the
    // player sees on screen. consumeBuffer still gates the activation, so:
    //   • grounded + buffer-consumed-by-player.update → orb does NOT fire (manual jump won)
    //   • mid-air + buffer-not-consumed → orb fires (replacing player velocity)
    this.processOrbActivation();

    // Ceiling collision (normal gravity — bump head)
    if (ceilingY !== null && !this.player.gravityFlipped) {
      const headY = this.player.position.y + this.player.size;
      if (headY >= ceilingY && this.player.velocity.y > 0) {
        this.player.position.y = ceilingY - this.player.size;
        this.player.velocity.y = 0;
      }
    }
    // Floor collision (inverted gravity — bump feet on floor)
    if (!this.player.gravityFlipped) {
      // Already handled in player.update
    } else {
      const feetY = this.player.position.y - this.player.size;
      if (feetY <= groundY && this.player.velocity.y < 0) {
        this.player.position.y = groundY + this.player.size;
        this.player.velocity.y = 0;
      }
    }

    // Landing impact particles
    if (this.player.justLanded) {
      this.particles.spawnLandingImpact(
        this.player.position.x, groundY, this.baseColor,
      );
    }

    // Jump SFX + input tracking
    if (this.player.justJumped) {
      this.sfx.jump();
      this.inputCount++;
    }

    // Ambient particles (spawn a mote every ~0.3s)
    this.ambientTimer += dt;
    if (this.ambientTimer > 0.3) {
      this.ambientTimer = 0;
      const cam = this.renderer.camera;
      this.particles.spawnAmbient(
        cam.position.x, cam.position.y,
        cam.viewWidth, cam.viewHeight,
        this.baseColor,
      );
    }

    // Update dynamic obstacles
    updateObstacles(this.obstacles, dt);

    // Collision detection
    const playerBounds = this.player.getBounds();
    this.grazeCooldown -= dt;

    for (const obs of this.obstacles) {
      const screenObs = { ...obs, x: obs.x - this.scrollOffset + gameTuning.respawnX };

      if (obs.type === 'jumppad') {
        const hit = checkJumpPadHit(playerBounds, prevFeetY, this.player.velocity.y, screenObs);
        if (hit) {
          this.firePad(obs, hit.topLand);
        }
        continue; // pads never deal damage and never graze
      }

      if (obs.type === 'orb') {
        // Orbs never kill, never block, never graze. Activation handled in processOrbActivation().
        continue;
      }

      if (checkCollision(playerBounds, screenObs)) {
        this.player.die();
        this.state.transition('dead');
        return;
      }
      // Graze detection
      if (this.grazeCooldown <= 0) {
        const dist = checkGraze(playerBounds, screenObs);
        if (dist !== null) {
          this.grazeCount++;
          this.peakGrazeStreak = Math.max(this.peakGrazeStreak, this.grazeCount);
          this.grazeCooldown = 0.15; // Don't spam graze particles
          this.particles.spawnGraze(
            this.player.position.x + this.player.size,
            this.player.position.y,
            { r: 1, g: 1, b: 0.5, a: 1 },
          );
          this.sfx.graze();
        }
      }
    }

    // Collectible collision
    for (const col of this.collectibles) {
      if (col.collected) continue;
      const cx = col.x - this.scrollOffset + gameTuning.respawnX;
      const bobY = col.y + Math.sin(col.bobPhase + this.scrollOffset * 0.01) * 8;
      const dx = this.player.position.x - cx;
      const dy = this.player.position.y - bobY;
      if (dx * dx + dy * dy < 400) { // ~20px pickup radius
        col.collected = true;
        this.sfx.starCollect();
        this.particles.spawnCollect(cx, bobY, { r: 1, g: 1, b: 0.3, a: 1 });
      }
    }

    // Process triggers
    this.processTriggers(dt);

    // Active text timer
    if (this.activeText) {
      this.activeText.timer -= dt * 1000;
      if (this.activeText.timer <= 0) {
        this.activeText = null;
      }
    }

    // Camera
    this.renderer.camera.leadOffset.x = gameTuning.cameraLeadX;
    this.renderer.camera.position.x = gameTuning.respawnX;
    this.renderer.camera.position.y = groundY + this.renderer.camera.viewHeight * 0.3;
    this.renderer.camera.update(dt);

    this.renderer.bloomStrength = gameTuning.bloomStrength;
    this.renderer.vignetteIntensity = gameTuning.vignetteIntensity;

    // Progress
    this.levelProgress = Math.min(this.scrollOffset / this.totalLength, 1);
    if (this.scrollOffset >= this.totalLength) {
      this.state.transition('complete');
      this.handleLevelComplete();
    }
  }

  private processTriggers(_dt: number): void {
    for (const trigger of this.triggers) {
      if (trigger.fired) continue;
      if (this.scrollOffset >= trigger.position) {
        trigger.fired = true;
        this.executeTrigger(trigger);
      }
    }
  }

  /**
   * Check each orb for overlap. If the player is overlapping AND a jump press
   * is buffered, the orb fires (consuming the buffer) — pre-empting any manual
   * jump that player.update would otherwise trigger this frame.
   */
  private processOrbActivation(): void {
    const playerBounds = this.player.getBounds();
    for (const obs of this.obstacles) {
      if (obs.type !== 'orb') continue;
      const screenObs = { ...obs, x: obs.x - this.scrollOffset + gameTuning.respawnX };
      if (!checkOrbOverlap(playerBounds, screenObs)) continue;
      if (!this.input.consumeBuffer('jump')) continue;
      this.fireOrb(obs);
      break; // one orb per jump press
    }
  }

  private fireOrb(orb: Obstacle): void {
    if (orb.orbSize === undefined) return;
    const tier: OrbSize = orb.orbSize;
    const multiplier = tier === 'small' ? 0.5 : tier === 'medium' ? 1.0 : 1.5;
    const launchSpeed = playerTuning.jumpVelocity * multiplier;
    this.player.velocity.y = this.player.gravityFlipped ? -launchSpeed : launchSpeed;
    this.player.grounded = false;
    orb.firedTimer = 0.25;
    this.sfx.boingOrb(tier);
  }

  private firePad(pad: Obstacle, topLand: boolean): void {
    if (pad.padSize === undefined) return;
    const baseTier: PadSize = pad.padSize;
    // Top-land upgrades by one tier; large becomes 'extra-large'
    const effectiveTier: 'small' | 'medium' | 'large' | 'extra-large' = topLand
      ? (baseTier === 'small' ? 'medium'
        : baseTier === 'medium' ? 'large'
        : 'extra-large')
      : baseTier;

    const multiplier =
      effectiveTier === 'small'      ? 0.5 :
      effectiveTier === 'medium'     ? 1.0 :
      effectiveTier === 'large'      ? 1.5 :
      2.0;  // extra-large

    // Launch — direction follows gravity (positive = up under normal gravity)
    const launchSpeed = playerTuning.jumpVelocity * multiplier;
    this.player.velocity.y = this.player.gravityFlipped ? -launchSpeed : launchSpeed;
    this.player.grounded = false;

    // Per-pad cooldown so a single contact doesn't re-fire every frame
    pad.padCooldown = 0.2;
    pad.firedTimer = 0.25;

    this.sfx.boing(effectiveTier);
  }

  private executeTrigger(trigger: RuntimeTrigger): void {
    switch (trigger.type) {
      case 'text': {
        const text = trigger.params['text'] as string;
        this.activeText = {
          text,
          timer: trigger.duration,
          duration: trigger.duration,
        };
        break;
      }
      case 'speed': {
        const speed = trigger.params['speed'] as number;
        this.speedMultiplier = speed;
        break;
      }
      case 'color': {
        const base = trigger.params['baseColor'] as string | undefined;
        const accent = trigger.params['accentColor'] as string | undefined;
        const background = trigger.params['backgroundColor'] as string | undefined;
        const duration = trigger.duration ?? 0;
        const easing = (trigger.easing ?? 'linear') as ColorEasing;
        if (duration <= 0) {
          // Instant apply (preserves the original behaviour when duration is 0).
          if (base) this.baseColor = hexToColor(base);
          if (accent) this.accentColor = hexToColor(accent);
          if (background) this.backgroundColor = hexToColor(background);
          this.colorTween = null;
        } else {
          // Snap any in-flight tween to current values so we start from a
          // coherent state, then start a fresh tween to the new targets.
          this.colorTween = {
            fromBase:       base       ? { ...this.baseColor }       : undefined,
            toBase:         base       ? hexToColor(base)            : undefined,
            fromAccent:     accent     ? { ...this.accentColor }     : undefined,
            toAccent:       accent     ? hexToColor(accent)          : undefined,
            fromBackground: background ? { ...this.backgroundColor } : undefined,
            toBackground:   background ? hexToColor(background)      : undefined,
            elapsed: 0,
            duration,
            easing,
          };
        }
        break;
      }
      case 'camera': {
        const action = trigger.params['action'] as string;
        if (action === 'shake') {
          const intensity = (trigger.params['intensity'] as number) ?? 10;
          this.renderer.camera.shake(intensity);
        }
        break;
      }
      case 'mode': {
        const targetMode = trigger.params['targetMode'] as string;
        if (targetMode === 'complex' && this.mode === 'real') {
          this.startTransition('complex');
        } else if (targetMode === 'real' && this.mode === 'complex') {
          this.startTransition('real');
        }
        break;
      }
      case 'music': {
        const action = trigger.params['action'] as string;
        if (action === 'add_layer') {
          const layer = trigger.params['layer'] as string;
          if (layer) this.audio.enableLayer(layer);
        } else if (action === 'remove_layer') {
          const layer = trigger.params['layer'] as string;
          if (layer) this.audio.disableLayer(layer);
        } else if (action === 'change_bpm') {
          const bpm = trigger.params['bpm'] as number;
          if (bpm && this.beatScheduler) this.beatScheduler.setBPM(bpm);
        }
        break;
      }
      case 'gravity': {
        const direction = trigger.params['direction'] as string;
        const strength = (trigger.params['strength'] as number) ?? 1.0;
        if (direction === 'up') {
          playerTuning.gravity = Math.abs(playerTuning.gravity) * strength;
        } else {
          playerTuning.gravity = -Math.abs(playerTuning.gravity) * strength;
        }
        break;
      }
      default:
        break;
    }
  }

  private handleLevelComplete(): void {
    const levelData = this.level?.data;
    if (!levelData) return;

    const starsCollected: [boolean, boolean, boolean] = [false, false, false];
    for (const c of this.collectibles) {
      if (c.type === 'star' && c.collected) {
        starsCollected[c.index] = true;
      }
    }

    const runData: RunData = {
      levelId: levelData.meta.id,
      difficulty: levelData.meta.difficulty,
      completed: true,
      timeMs: performance.now() - this.runStartTime,
      parTimeMs: levelData.meta.parTime,
      optimalInputs: levelData.meta.optimalInputs,
      inputCount: this.inputCount,
      starsCollected,
      fragmentsCollected: [],
      grazeCount: this.grazeCount,
      peakGrazeStreak: this.peakGrazeStreak,
      progress: 1,
      practiceUsedThisRun: this.practiceUsedThisRun,
    };

    const breakdown = calculateScore(runData);
    this.beatScheduler?.stop();
    this.onLevelComplete?.(breakdown, runData);
  }

  private updateDead(dt: number): void {
    this.deathTimer += dt;
    this.renderer.camera.update(dt);

    if (this.deathTimer > gameTuning.deathPause) {
      if (this.input.consumeBuffer('jump') || this.deathTimer > 2.0) {
        this.restart();
      }
    }
  }

  private restart(): void {
    if (this.practiceMode && this.lastCheckpointScroll > 0) {
      this.practiceUsedThisRun = true;
      // Respawn at checkpoint
      this.state.reset('playing');
      this.scrollOffset = this.lastCheckpointScroll;
      const groundY = this.level
        ? getGroundY(this.level.data.segments, this.level.segmentOffsets, this.scrollOffset, gameTuning.groundY)
        : gameTuning.groundY;
      this.player.respawn(gameTuning.respawnX, groundY + this.player.size + 50);
    } else {
      // Restart from beginning — full state reset
      this.state.reset('playing');
      this.scrollOffset = 0;
      this.practiceUsedThisRun = false;
      this.speedMultiplier = this.level?.data.settings.initialSpeed ?? 1.0;
      // Reset gravity, mode, colors to level defaults
      playerTuning.gravity = -Math.abs(playerTuning.gravity);
      this.mode = this.level?.data.settings.initialMode === 'complex' ? 'complex' : 'real';
      this.input.touchMode = this.mode === 'complex' ? 'directional' : 'jump';
      this.transitionProgress = -1;
      if (this.level) {
        this.baseColor = this.level.baseColor;
        this.accentColor = this.level.accentColor;
        this.backgroundColor = this.level.backgroundColor;
      }
      this.colorTween = null;
      this.player3dX = 0;
      this.player3dY = 0;
      this.tunnel.scrollZ = 0;
      this.player.respawn(gameTuning.respawnX, gameTuning.groundY + this.player.size + 50);
      // Reset triggers and collectibles
      for (const t of this.triggers) { t.fired = false; t.progress = 0; }
      for (const c of this.collectibles) { c.collected = false; }
    }
    this.particles.clear();
    this.activeText = null;
  }

  // ─── Complex Mode ──────────────────────────────────────

  private startTransition(targetMode: 'real' | 'complex'): void {
    this.transitionProgress = 0;
    this.renderer.chromaticAberration = 0.03;
    // Set the target — mode switches at midpoint
    if (targetMode === 'complex') {
      this.player3dX = 0;
      this.player3dY = 0;
      this.input.touchMode = 'directional';
      this.tunnel.scrollZ = 0;
      this.tunnel.color = { ...this.baseColor, a: 0.5 };
      this.tunnel.ringColor = { ...this.baseColor, a: 0.3 };
      this.tunnel.lineColor = { ...this.baseColor, a: 0.15 };
    } else {
      this.input.touchMode = 'jump';
    }
    // Store target in a way the update can read
    this._transitionTarget = targetMode;
  }

  private _transitionTarget: 'real' | 'complex' = 'complex';

  private updateTransition(dt: number): void {
    if (this.transitionProgress < 0) return;
    this.transitionProgress += dt / this.transitionDuration;

    // Visual effects during transition
    const t = this.transitionProgress;
    this.renderer.chromaticAberration = 0.03 * Math.sin(t * Math.PI);
    this.renderer.beatPulse = Math.sin(t * Math.PI) * 0.5;

    // Switch mode at midpoint
    if (t >= 0.5 && this.mode !== this._transitionTarget) {
      this.mode = this._transitionTarget;
    }

    if (t >= 1) {
      this.transitionProgress = -1;
      this.renderer.chromaticAberration = 0;
    }
  }

  private updateComplex(dt: number): void {
    const effectiveSpeed = gameTuning.scrollSpeed * this.speedMultiplier;
    this.scrollOffset += effectiveSpeed * dt;
    this.tunnel.scrollZ += effectiveSpeed * dt;

    // Player movement on 2D plane (left/right = real axis, up/down = imaginary axis)
    const moveSpeed = 120;
    if (this.input.isHeld('left')) this.player3dX -= moveSpeed * dt;
    if (this.input.isHeld('right')) this.player3dX += moveSpeed * dt;
    if (this.input.isHeld('jump') || this.input.isHeld('up')) this.player3dY += moveSpeed * dt;
    if (this.input.isHeld('duck') || this.input.isHeld('down')) this.player3dY -= moveSpeed * dt;

    // Clamp to tunnel radius
    const maxR = this.tunnel.radius * 0.7;
    const dist = Math.sqrt(this.player3dX * this.player3dX + this.player3dY * this.player3dY);
    if (dist > maxR) {
      this.player3dX *= maxR / dist;
      this.player3dY *= maxR / dist;
    }

    // Check 3D obstacle collisions
    for (const obs of this.tunnelObstacles) {
      if (obs.passed) continue;
      const obsScreenZ = obs.z - this.tunnel.scrollZ;
      if (obsScreenZ < -5) {
        obs.passed = true;
        continue;
      }
      if (obsScreenZ > 5) continue; // Not reached yet

      if (this.tunnel.checkCollision(this.player3dX, this.player3dY, 5, obs)) {
        this.player.die();
        this.renderer.camera3d.shake(gameTuning.shakeIntensity);
        this.state.transition('dead');
        return;
      }
    }

    // Update dynamic obstacles, triggers, etc.
    this.processTriggers(dt);
    this.updateTransition(dt);

    // Camera
    this.renderer.camera3d.update(dt);
    this.renderer.bloomStrength = gameTuning.bloomStrength;
    this.renderer.vignetteIntensity = gameTuning.vignetteIntensity;

    // Progress
    this.levelProgress = Math.min(this.scrollOffset / this.totalLength, 1);
    if (this.scrollOffset >= this.totalLength) {
      this.state.transition('complete');
      this.handleLevelComplete();
    }
  }

  private renderComplex(): void {
    const r = this.renderer;
    r.beginFrame(this.backgroundColor);

    // Render tunnel
    this.tunnel.render(r);

    // Render 3D obstacles
    for (const obs of this.tunnelObstacles) {
      this.tunnel.renderObstacle(r, obs);
    }

    // Render player in tunnel
    if (this.player.alive) {
      this.tunnel.renderPlayer3d(r, this.player3dX, this.player3dY, this.player.color);
    }

    // Flush 3D batch
    r.flush3dBatch();

    // 2D HUD on top (progress bar, combo)
    this.renderProgressBar();
    this.renderComboHUD();

    if (this.activeText) {
      this.renderTriggerText();
    }

    r.endFrame();

    if (this.showFps && this.fpsElement) {
      this.fpsElement.textContent = `${this.engine.fps} FPS`;
    }
  }

  // ─── Rendering ──────────────────────────────────────────

  private render(_interpolation: number): void {
    // Use Complex mode renderer when in 3D mode
    if (this.mode === 'complex' && this.transitionProgress < 0) {
      this.renderComplex();
      return;
    }

    const r = this.renderer;
    r.beginFrame(this.backgroundColor);

    const cam = r.camera;

    // Parallax background — decorative trig function waves
    this.renderParallaxBackground();

    // Grid
    const gridBaseY = this.level
      ? getGroundY(this.level.data.segments, this.level.segmentOffsets, this.scrollOffset, gameTuning.groundY)
      : gameTuning.groundY;
    r.drawGrid(gridBaseY, this.baseColor, gameTuning.gridSpacing, gameTuning.gridIntensity);

    // Terrain rendering (trig function surfaces)
    if (this.level) {
      renderTerrain(
        r,
        this.level.data.segments,
        this.level.segmentOffsets,
        this.scrollOffset,
        gameTuning.respawnX,
        cam.position.x - cam.viewWidth,
        cam.position.x + cam.viewWidth,
        gameTuning.groundY,
        this.baseColor,
      );
    } else {
      // Fallback: flat ground line
      r.drawLine(
        cam.position.x - cam.viewWidth, gameTuning.groundY,
        cam.position.x + cam.viewWidth, gameTuning.groundY,
        2, this.baseColor,
      );
    }

    // Obstacles
    for (const obs of this.obstacles) {
      const sx = obs.x - this.scrollOffset + gameTuning.respawnX;
      if (sx > cam.position.x - cam.viewWidth - obs.width &&
          sx < cam.position.x + cam.viewWidth + obs.width) {
        this.renderObstacle(obs, sx);
      }
    }

    // Collectibles
    this.renderCollectibles();

    // Player trail
    this.renderTrail();

    // Player
    if (this.player.alive) {
      this.renderPlayer();
    }

    // Particles
    this.particles.render(r);

    // HUD
    this.renderProgressBar();
    this.renderComboHUD();

    // Trigger text
    if (this.activeText) {
      this.renderTriggerText();
    }

    r.endFrame();

    if (this.showFps && this.fpsElement) {
      this.fpsElement.textContent = `${this.engine.fps} FPS`;
    }
  }

  private renderParallaxBackground(): void {
    const r = this.renderer;
    const cam = r.camera;
    const cx = cam.position.x;
    const cy = cam.position.y;
    const vw = cam.viewWidth;
    const vh = cam.viewHeight;
    const step = 8;
    const bottom = cy - vh; // Well below screen

    // Hills derive from the current backgroundColor so color-trigger changes
    // cascade automatically. Farthest is pale/hazy, nearest is a bold silhouette.
    const hue = rgbToHue(this.backgroundColor);
    const [farColor, midColor, nearColor] = deriveHillPalette(hue);

    // Back-to-front painter's algorithm: each layer fills below its wave,
    // occluding the layers behind it.
    const layers: {
      offset: number; freq: number; amp: number; baseY: number;
      fillColor: Color; fn: (x: number) => number;
    }[] = [
      {
        offset: this.scrollOffset * 0.15,
        freq: 0.008, amp: 60, baseY: cy - vh * 0.05,
        fillColor: farColor,
        fn: Math.sin,
      },
      {
        offset: this.scrollOffset * 0.3,
        freq: 0.012, amp: 40, baseY: cy + vh * 0.05,
        fillColor: midColor,
        fn: Math.cos,
      },
      {
        offset: this.scrollOffset * 0.5,
        freq: 0.02, amp: 25, baseY: cy - vh * 0.1,
        fillColor: nearColor,
        fn: Math.sin,
      },
    ];

    for (const layer of layers) {
      const { offset, freq, amp, baseY, fillColor, fn } = layer;
      for (let sx = cx - vw; sx < cx + vw; sx += step) {
        const y1 = baseY + fn((sx + offset) * freq) * amp;
        const y2 = baseY + fn((sx + step + offset) * freq) * amp;
        r.drawTriangle(sx, y1, sx + step, y2, sx, bottom, fillColor);
        r.drawTriangle(sx + step, y2, sx + step, bottom, sx, bottom, fillColor);
      }
    }
  }

  private renderObstacle(obs: Obstacle, sx: number): void {
    const r = this.renderer;
    switch (obs.type) {
      case 'spike':
        this.renderSpike(sx, obs.y, obs.width, obs.height, obs.color, obs.angle);
        break;
      case 'wall':
        this.renderWall(sx, obs.y, obs.width, obs.height, obs.color, obs.angle);
        break;
      case 'jumppad':
        this.renderJumpPad(sx, obs.y, obs.width, obs.height, obs.color, obs.firedTimer ?? 0);
        break;
      case 'orb':
        this.renderOrb(sx, obs.y, obs.width, obs.color, obs.firedTimer ?? 0);
        break;
      case 'moving':
      case 'rotating':
      case 'wave':
        if (obs.type === 'moving') {
          this.renderWall(sx, obs.y, obs.width, obs.height, obs.color, obs.angle);
        } else {
          r.drawRect(sx, obs.y, obs.width, obs.height, obs.color);
        }
        break;
    }
  }

  private renderPlayer(): void {
    const verts = this.player.getShapeVertices();
    const cx = this.player.position.x;
    const cy = this.player.position.y;

    // Convergence combo glow — intensifies with graze count
    const comboIntensity = Math.min(this.grazeCount / 20, 1);
    if (comboIntensity > 0.05) {
      const comboGlow: Color = { r: 1, g: 1, b: 0.5, a: 0.08 + comboIntensity * 0.15 };
      const gs = 1.5 + comboIntensity * 0.8;
      this.drawPolygon(verts, cx, cy, gs, comboGlow);
    }

    // Standard glow (slightly larger, lower alpha)
    const glowColor: Color = { ...this.player.color, a: 0.3 };
    this.drawPolygon(verts, cx, cy, 1.3, glowColor);

    // Player body
    this.drawPolygon(verts, cx, cy, 1.0, this.player.color);

    // Googly eyes overlay (Task 7).
    if (this.player.hasGooglyEyes) {
      this.renderGooglyEyes(cx, cy);
    }
  }

  /**
   * Draw an N-vertex polygon as a triangle fan from vertex 0. `scale` is applied
   * radially around (cx, cy) so the same verts can be reused for glow scaling.
   */
  private drawPolygon(verts: Vec2[], cx: number, cy: number, scale: number, color: Color): void {
    for (let i = 1; i < verts.length - 1; i++) {
      const v0 = verts[0], v1 = verts[i], v2 = verts[i + 1];
      this.renderer.drawTriangle(
        cx + (v0.x - cx) * scale, cy + (v0.y - cy) * scale,
        cx + (v1.x - cx) * scale, cy + (v1.y - cy) * scale,
        cx + (v2.x - cx) * scale, cy + (v2.y - cy) * scale,
        color,
      );
    }
  }

  /**
   * Two white "eye" disks with black pupils that lean in the direction the
   * player is moving. Drawn after the body so they always sit on top.
   */
  private renderGooglyEyes(cx: number, cy: number): void {
    const s = this.player.size;
    const cos = Math.cos(this.player.rotation);
    const sin = Math.sin(this.player.rotation);

    // Eye centers in local space — two dots near the "front" of the body.
    const localEyes: [number, number][] = [
      [s * 0.25, -s * 0.35],
      [s * 0.25,  s * 0.35],
    ];
    const eyeRadius = s * 0.32;
    const pupilRadius = s * 0.16;

    // Pupil lean: small offset in the player's velocity direction.
    const vy = this.player.velocity.y;
    const speed = Math.hypot(this.player.effectiveScrollSpeed, vy);
    const leanX = (speed > 0 ? this.player.effectiveScrollSpeed / speed : 1) * pupilRadius * 0.4;
    const leanY = (speed > 0 ? vy / speed : 0) * pupilRadius * 0.4;

    const white: Color = { r: 1, g: 1, b: 1, a: 1 };
    const black: Color = { r: 0, g: 0, b: 0, a: 1 };

    for (const [lx, ly] of localEyes) {
      const ex = cx + lx * cos - ly * sin;
      const ey = cy + lx * sin + ly * cos;
      this.drawDisk(ex, ey, eyeRadius, white);
      this.drawDisk(ex + leanX, ey + leanY, pupilRadius, black);
    }
  }

  /** Draw a small filled disk as an 8-segment triangle fan. */
  private drawDisk(cx: number, cy: number, radius: number, color: Color): void {
    const N = 8;
    for (let i = 0; i < N; i++) {
      const a0 = (i / N) * Math.PI * 2;
      const a1 = ((i + 1) / N) * Math.PI * 2;
      this.renderer.drawTriangle(
        cx, cy,
        cx + Math.cos(a0) * radius, cy + Math.sin(a0) * radius,
        cx + Math.cos(a1) * radius, cy + Math.sin(a1) * radius,
        color,
      );
    }
  }

  private renderTrail(): void {
    const trail = this.player.trail;
    const rx = gameTuning.respawnX;
    const curScroll = this.scrollOffset;
    const rainbow = this.player.rainbowTrail;
    const tSec = performance.now() / 1000;

    for (let i = 1; i < trail.length; i++) {
      const alpha = (i / trail.length) * 0.5;
      let color: Color;
      if (rainbow) {
        // One full hue rotation per ~1.5s, plus per-trail-point offset for a
        // visible gradient along the trail.
        const hue = ((tSec * 0.667) + (i / trail.length) * 0.6) % 1;
        const c = hslToColor(hue, 0.85, 0.55);
        color = { ...c, a: alpha };
      } else {
        color = { ...this.player.color, a: alpha };
      }
      const thickness = (i / trail.length) * 3;
      const x0 = rx - (curScroll - trail[i - 1].x);
      const x1 = rx - (curScroll - trail[i].x);
      this.renderer.drawLine(x0, trail[i - 1].y, x1, trail[i].y, thickness, color);
    }
  }

  /** Rotate a point around a pivot by the given angle */
  private rotAround(px: number, py: number, pivotX: number, pivotY: number, cos: number, sin: number): [number, number] {
    const lx = px - pivotX;
    const ly = py - pivotY;
    return [pivotX + lx * cos - ly * sin, pivotY + lx * sin + ly * cos];
  }

  private renderSpike(x: number, y: number, w: number, h: number, color: Color, angle: number): void {
    const pivotX = x + w / 2;
    const pivotY = y;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const R = (px: number, py: number) => this.rotAround(px, py, pivotX, pivotY, cos, sin);

    const tip = R(x + w / 2, y + h);
    const bl = R(x, y);
    const br = R(x + w, y);

    // Spike body
    this.renderer.drawTriangle(tip[0], tip[1], bl[0], bl[1], br[0], br[1], color);

    // Glow outline (slightly larger, drawn behind)
    const glowColor: Color = { ...color, a: 0.3 };
    const gx = 3;
    const gTip = R(x + w / 2, y + h + gx);
    const gBl = R(x - gx, y - gx);
    const gBr = R(x + w + gx, y - gx);
    this.renderer.drawTriangle(gTip[0], gTip[1], gBl[0], gBl[1], gBr[0], gBr[1], glowColor);

    // Neon edge lines
    const edgeColor: Color = { ...color, a: 0.8 };
    this.renderer.drawLine(bl[0], bl[1], tip[0], tip[1], 1.5, edgeColor);
    this.renderer.drawLine(tip[0], tip[1], br[0], br[1], 1.5, edgeColor);
    this.renderer.drawLine(bl[0], bl[1], br[0], br[1], 1.0, edgeColor);
  }

  private renderOrb(x: number, y: number, diameter: number, color: Color, firedTimer: number): void {
    const r = this.renderer;
    const cx = x + diameter / 2;
    const cy = y + diameter / 2;
    const baseR = diameter / 2;

    // Subtle breath pulse — 5% scale variation based on world-x so each orb is offset
    const breath = 1.0 + Math.sin(this.scrollOffset * 0.03 + x * 0.1) * 0.05;

    // Fired flash — brief radial expansion (1.0 → 1.3 → 1.0)
    const FIRED_DURATION = 0.25;
    let firedScale = 1.0;
    let firedAlpha = 1.0;
    if (firedTimer > 0) {
      const t = 1 - (firedTimer / FIRED_DURATION); // 0..1
      firedScale = 1.0 + Math.sin(t * Math.PI) * 0.3;
      firedAlpha = 1.0 + Math.sin(t * Math.PI) * 0.3;
    }

    const radius = baseR * breath * firedScale;
    const segments = 24;

    // Pre-compute ring vertices
    const ring: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      ring.push([cx + radius * Math.cos(theta), cy + radius * Math.sin(theta)]);
    }

    // Glow halo — same disk, larger, faint
    const glowColor: Color = { ...color, a: 0.25 };
    const glowR = radius * 1.4;
    for (let i = 0; i < segments; i++) {
      const theta1 = (i / segments) * Math.PI * 2;
      const theta2 = ((i + 1) / segments) * Math.PI * 2;
      r.drawTriangle(
        cx, cy,
        cx + glowR * Math.cos(theta1), cy + glowR * Math.sin(theta1),
        cx + glowR * Math.cos(theta2), cy + glowR * Math.sin(theta2),
        glowColor,
      );
    }

    // Filled disk
    const fillColor: Color = { ...color, a: 0.6 };
    for (let i = 0; i < segments; i++) {
      const [ax, ay] = ring[i];
      const [bx, by] = ring[i + 1];
      r.drawTriangle(cx, cy, ax, ay, bx, by, fillColor);
    }

    // Inner ring highlight (small bright disk at center for visual focus)
    const innerColor: Color = { ...color, a: 0.9 * Math.min(1, firedAlpha) };
    const innerR = radius * 0.45;
    for (let i = 0; i < segments; i++) {
      const theta1 = (i / segments) * Math.PI * 2;
      const theta2 = ((i + 1) / segments) * Math.PI * 2;
      r.drawTriangle(
        cx, cy,
        cx + innerR * Math.cos(theta1), cy + innerR * Math.sin(theta1),
        cx + innerR * Math.cos(theta2), cy + innerR * Math.sin(theta2),
        innerColor,
      );
    }

    // Neon rim
    const rimColor: Color = { ...color, a: Math.min(1, 0.95 * firedAlpha) };
    for (let i = 0; i < segments; i++) {
      const [ax, ay] = ring[i];
      const [bx, by] = ring[i + 1];
      r.drawLine(ax, ay, bx, by, 1.5, rimColor);
    }
  }

  private renderJumpPad(x: number, y: number, w: number, h: number, color: Color, firedTimer: number): void {
    const r = this.renderer;
    const cx = x + w / 2;
    const baseY = y;

    // Squish-and-rebound: scaleY follows 1.0 → 0.6 → 1.0 (with overshoot) over the firedTimer lifetime
    const FIRED_DURATION = 0.25;
    let scaleY = 1.0;
    if (firedTimer > 0) {
      const t = 1 - (firedTimer / FIRED_DURATION); // 0..1 over the animation
      if (t < 0.5) {
        const u = t / 0.5;
        scaleY = 1.0 + (0.6 - 1.0) * u; // 1.0 → 0.6
      } else {
        const u = (t - 0.5) / 0.5;
        scaleY = 0.6 + (1.0 - 0.6) * u + Math.sin(u * Math.PI) * 0.15; // 0.6 → 1.0 + bounce
      }
    }

    const segments = 16;
    const rx = w / 2;
    const ry = h * scaleY;

    // Half-ellipse fan vertices (theta from 0 to PI)
    const vertices: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI;
      vertices.push([cx - rx * Math.cos(theta), baseY + ry * Math.sin(theta)]);
    }

    // Glow halo — same shape scaled 1.25× from base center
    const glowColor: Color = { ...color, a: 0.3 };
    const glowScale = 1.25;
    for (let i = 0; i < segments; i++) {
      const [ax, ay] = vertices[i];
      const [bx, by] = vertices[i + 1];
      const gax = cx + (ax - cx) * glowScale;
      const gay = baseY + (ay - baseY) * glowScale;
      const gbx = cx + (bx - cx) * glowScale;
      const gby = baseY + (by - baseY) * glowScale;
      r.drawTriangle(cx, baseY, gax, gay, gbx, gby, glowColor);
    }

    // Filled cap
    const fillColor: Color = { ...color, a: 0.85 };
    for (let i = 0; i < segments; i++) {
      const [ax, ay] = vertices[i];
      const [bx, by] = vertices[i + 1];
      r.drawTriangle(cx, baseY, ax, ay, bx, by, fillColor);
    }

    // Neon rim — bright when firing, normal otherwise
    const rimAlpha = firedTimer > 0 ? 1.0 : 0.95;
    const rimColor: Color = { ...color, a: rimAlpha };
    for (let i = 0; i < segments; i++) {
      const [ax, ay] = vertices[i];
      const [bx, by] = vertices[i + 1];
      r.drawLine(ax, ay, bx, by, 1.5, rimColor);
    }
    // Base line
    r.drawLine(vertices[0][0], vertices[0][1], vertices[segments][0], vertices[segments][1], 1.0, rimColor);
  }

  private renderWall(x: number, y: number, w: number, h: number, color: Color, angle: number): void {
    const pivotX = x + w / 2;
    const pivotY = y;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const R = (px: number, py: number) => this.rotAround(px, py, pivotX, pivotY, cos, sin);

    const bl = R(x, y);
    const br = R(x + w, y);
    const tr = R(x + w, y + h);
    const tl = R(x, y + h);

    // Filled wall (two triangles)
    const fillColor: Color = { ...color, a: 0.6 };
    this.renderer.drawTriangle(bl[0], bl[1], br[0], br[1], tl[0], tl[1], fillColor);
    this.renderer.drawTriangle(br[0], br[1], tr[0], tr[1], tl[0], tl[1], fillColor);

    // Neon edge outlines
    const edgeColor: Color = { ...color, a: 0.9 };
    this.renderer.drawLine(bl[0], bl[1], br[0], br[1], 1.5, edgeColor);
    this.renderer.drawLine(br[0], br[1], tr[0], tr[1], 1.5, edgeColor);
    this.renderer.drawLine(tr[0], tr[1], tl[0], tl[1], 1.5, edgeColor);
    this.renderer.drawLine(tl[0], tl[1], bl[0], bl[1], 1.5, edgeColor);
  }

  private renderCollectibles(): void {
    const r = this.renderer;
    for (const col of this.collectibles) {
      if (col.collected) continue;
      const sx = col.x - this.scrollOffset + gameTuning.respawnX;
      const cam = r.camera;
      if (sx < cam.position.x - cam.viewWidth - 20 ||
          sx > cam.position.x + cam.viewWidth + 20) continue;

      const bobY = col.y + Math.sin(col.bobPhase + this.scrollOffset * 0.01) * 8;

      if (col.type === 'star') {
        this.renderStar(sx, bobY);
      }
    }
  }

  private renderStar(x: number, y: number): void {
    // 5-pointed star as overlapping triangles
    const starColor: Color = { r: 1, g: 1, b: 0.3, a: 1 };
    const glowColor: Color = { r: 1, g: 1, b: 0.3, a: 0.25 };
    const s = 8;

    // Glow circle
    this.renderDiamond(x, y, s * 1.8, glowColor);
    // Star body
    this.renderDiamond(x, y, s, starColor);
  }

  private renderDiamond(x: number, y: number, s: number, color: Color): void {
    this.renderer.drawTriangle(x, y + s, x - s * 0.6, y, x + s * 0.6, y, color);
    this.renderer.drawTriangle(x, y - s, x - s * 0.6, y, x + s * 0.6, y, color);
  }

  private renderProgressBar(): void {
    const cam = this.renderer.camera;
    const barWidth = cam.viewWidth * 0.6;
    const barHeight = 4;
    const barX = cam.position.x - barWidth / 2;
    const barY = cam.position.y + cam.viewHeight / 2 - 20;

    // Background
    this.renderer.drawRect(barX, barY, barWidth, barHeight, { r: 1, g: 1, b: 1, a: 0.1 });

    // Progress fill
    this.renderer.drawRect(barX, barY, barWidth * this.levelProgress, barHeight, this.baseColor);

    // Percentage text indicator (small tick mark)
    const pct = Math.floor(this.levelProgress * 100);
    if (pct > 0) {
      const tickX = barX + barWidth * this.levelProgress;
      this.renderer.drawRect(tickX - 1, barY - 2, 2, barHeight + 4, this.baseColor);
    }
  }

  private renderComboHUD(): void {
    if (this.grazeCount === 0) return;
    const cam = this.renderer.camera;

    // Convergence multiplier tiers
    let multiplier = 1.0;
    let tierName = '';
    if (this.grazeCount >= 51) { multiplier = 5.0; tierName = 'CONVERGENT'; }
    else if (this.grazeCount >= 31) { multiplier = 4.0; tierName = 'CONVERGING'; }
    else if (this.grazeCount >= 16) { multiplier = 3.0; tierName = 'RESONANT'; }
    else if (this.grazeCount >= 6) { multiplier = 2.0; tierName = 'HARMONIC'; }
    else if (this.grazeCount >= 1) { multiplier = 1.5; tierName = 'GRAZING'; }

    // Update combo DOM overlay
    this.updateComboOverlay(this.grazeCount, multiplier, tierName);

    // Graze spark indicators near progress bar
    const x = cam.position.x + cam.viewWidth * 0.32;
    const y = cam.position.y + cam.viewHeight / 2 - 28;
    const count = Math.min(this.grazeCount, 15);
    for (let i = 0; i < count; i++) {
      const alpha = 0.4 + (i / count) * 0.5;
      this.renderer.drawRect(
        x + i * 5, y, 3, 3,
        { r: 1, g: 1, b: 0.5, a: alpha },
      );
    }
  }

  private comboOverlay: HTMLElement | null = null;

  private updateComboOverlay(count: number, multiplier: number, tier: string): void {
    if (!this.comboOverlay) {
      this.comboOverlay = document.getElementById('combo-hud');
      if (!this.comboOverlay) {
        this.comboOverlay = document.createElement('div');
        this.comboOverlay.id = 'combo-hud';
        this.comboOverlay.style.cssText = `
          position: fixed; top: 80px; right: 12px;
          color: #ffdd33; font-family: 'Courier New', monospace;
          font-size: 13px; font-weight: bold;
          text-shadow: 0 0 10px #ffdd3388;
          pointer-events: none; z-index: 50; text-align: right;
          transition: opacity 0.2s;
        `;
        document.body.appendChild(this.comboOverlay);
      }
    }
    this.comboOverlay.style.opacity = count > 0 ? '1' : '0';
    this.comboOverlay.innerHTML = `
      <div style="font-size: 10px; color: #ffdd33aa;">${tier}</div>
      <div>×${multiplier.toFixed(1)} <span style="font-size:10px; color:#aaa">(${count})</span></div>
    `;
  }

  private renderTriggerText(): void {
    if (!this.activeText) return;
    const fadeAlpha = Math.min(this.activeText.timer / 500, 1);
    if (fadeAlpha > 0.01) {
      this.updateTextOverlay(this.activeText.text, fadeAlpha);
    }
  }

  private textOverlay: HTMLElement | null = null;

  private updateTextOverlay(text: string, alpha: number): void {
    if (!this.textOverlay) {
      this.textOverlay = document.getElementById('trigger-text');
      if (!this.textOverlay) {
        this.textOverlay = document.createElement('div');
        this.textOverlay.id = 'trigger-text';
        this.textOverlay.style.cssText = `
          position: fixed; top: 30%; left: 50%; transform: translate(-50%, -50%);
          color: #00ffff; font-family: 'Courier New', monospace; font-size: 24px;
          font-weight: bold; text-shadow: 0 0 20px #00ffff88, 0 0 40px #00ffff44;
          pointer-events: none; z-index: 50; text-align: center;
          transition: opacity 0.3s;
        `;
        document.body.appendChild(this.textOverlay);
      }
    }
    this.textOverlay.textContent = text;
    this.textOverlay.style.opacity = String(Math.min(1, alpha));
  }

  // ─── Setup ──────────────────────────────────────────────

  private setupFpsDisplay(): void {
    this.fpsElement = document.getElementById('fps');
    if (!this.fpsElement) {
      this.fpsElement = document.createElement('div');
      this.fpsElement.id = 'fps';
      this.fpsElement.style.cssText = `
        position: fixed; top: 54px; right: 8px;
        color: #0ff; font-family: monospace; font-size: 12px;
        opacity: 0.6; pointer-events: none; z-index: 100;
      `;
      document.body.appendChild(this.fpsElement);
    }
  }

  private setupTuningPanel(): void {
    const params: TunableParam[] = [
      // Player physics
      { label: 'Gravity', key: 'player.gravity', min: -4000, max: -500, step: 50,
        get: () => playerTuning.gravity, set: (v) => { playerTuning.gravity = v; } },
      { label: 'Jump Velocity', key: 'player.jumpVelocity', min: 200, max: 1500, step: 10,
        get: () => playerTuning.jumpVelocity, set: (v) => { playerTuning.jumpVelocity = v; } },
      { label: 'Coyote Time', key: 'player.coyoteTime', min: 0, max: 0.3, step: 0.01,
        get: () => playerTuning.coyoteTime, set: (v) => { playerTuning.coyoteTime = v; } },
      { label: 'Player Size', key: 'player.playerSize', min: 4, max: 40, step: 1,
        get: () => playerTuning.playerSize, set: (v) => { playerTuning.playerSize = v; } },
      { label: 'Rotation Lerp', key: 'player.rotationLerp', min: 1, max: 30, step: 0.5,
        get: () => playerTuning.rotationLerp, set: (v) => { playerTuning.rotationLerp = v; } },
      { label: 'Ground Rot Snap', key: 'player.groundRotationSnap', min: 1, max: 30, step: 1,
        get: () => playerTuning.groundRotationSnap, set: (v) => { playerTuning.groundRotationSnap = v; } },
      { label: 'Trail Length', key: 'player.trailLength', min: 0, max: 80, step: 1,
        get: () => playerTuning.trailLength, set: (v) => { playerTuning.trailLength = v; } },

      // Game / world
      { label: 'Scroll Speed', key: 'game.scrollSpeed', min: 50, max: 800, step: 10,
        get: () => gameTuning.scrollSpeed, set: (v) => { gameTuning.scrollSpeed = v; } },
      { label: 'Ground Y', key: 'game.groundY', min: 20, max: 200, step: 5,
        get: () => gameTuning.groundY, set: (v) => { gameTuning.groundY = v; } },
      { label: 'Death Pause', key: 'game.deathPause', min: 0, max: 2, step: 0.1,
        get: () => gameTuning.deathPause, set: (v) => { gameTuning.deathPause = v; } },
      { label: 'Shake Intensity', key: 'game.shakeIntensity', min: 0, max: 40, step: 1,
        get: () => gameTuning.shakeIntensity, set: (v) => { gameTuning.shakeIntensity = v; } },
      { label: 'Camera Lead X', key: 'game.cameraLeadX', min: 0, max: 500, step: 10,
        get: () => gameTuning.cameraLeadX, set: (v) => { gameTuning.cameraLeadX = v; } },
      { label: 'Speed Multi', key: 'game.speedMultiplier', min: 0.5, max: 3, step: 0.05,
        get: () => this.speedMultiplier, set: (v) => { this.speedMultiplier = v; } },

      // Visuals
      { label: 'Bloom Strength', key: 'visuals.bloomStrength', min: 0, max: 2, step: 0.05,
        get: () => gameTuning.bloomStrength, set: (v) => { gameTuning.bloomStrength = v; } },
      { label: 'Vignette', key: 'visuals.vignetteIntensity', min: 0, max: 1, step: 0.05,
        get: () => gameTuning.vignetteIntensity, set: (v) => { gameTuning.vignetteIntensity = v; } },
      { label: 'Grid Spacing', key: 'visuals.gridSpacing', min: 10, max: 150, step: 5,
        get: () => gameTuning.gridSpacing, set: (v) => { gameTuning.gridSpacing = v; } },
      { label: 'Grid Intensity', key: 'visuals.gridIntensity', min: 0, max: 1, step: 0.05,
        get: () => gameTuning.gridIntensity, set: (v) => { gameTuning.gridIntensity = v; } },
    ];

    this.tuningPanel.register(params);
    this.tuningPanel.captureDefaults();
  }
}

/**
 * Skip triggers and collectibles that are positioned before a given scroll offset.
 * Used by play-test mode to resume mid-level without re-firing already-passed events.
 */
export function applyStartOffset(
  triggers: RuntimeTrigger[],
  collectibles: RuntimeCollectible[],
  startOffset: number,
): void {
  if (startOffset <= 0) return;
  for (const t of triggers) {
    if (t.position < startOffset) t.fired = true;
  }
  for (const c of collectibles) {
    if (c.x < startOffset) c.collected = true;
  }
}
